'use client';

import { NavLink } from "react-router";
import { cn } from "../ui";
import type { SidebarItem } from "./types";

function flattenAllWithHref(items: SidebarItem[]): SidebarItem[] {
  const out: SidebarItem[] = [];
  function walk(list: SidebarItem[]) {
    for (const it of list) {
      if (it.href) out.push(it);
      if (it.children?.length) walk(it.children);
    }
  }
  walk(items);
  return out;
}

const TENANT_BOTTOM_HREFS = ["/", "/pos", "/sales", "/inventory", "/products"] as const;
const ADMIN_BOTTOM_HREFS = ["/admin", "/admin/users", "/admin/businesses", "/admin/reports", "/admin/verifications"] as const;

function pickBottomNavItems(items: SidebarItem[], isSuperAdmin: boolean): SidebarItem[] {
  const flat = flattenAllWithHref(items);
  const preferred = isSuperAdmin ? ADMIN_BOTTOM_HREFS : TENANT_BOTTOM_HREFS;
  const picked: SidebarItem[] = [];
  for (const href of preferred) {
    const found = flat.find((i) => i.href === href);
    if (found) picked.push(found);
    if (picked.length >= 5) break;
  }
  return picked;
}

interface BottomNavProps {
  items: SidebarItem[];
  isSuperAdmin: boolean;
}

export function BottomNav({ items, isSuperAdmin }: BottomNavProps) {
  const navItems = pickBottomNavItems(items, isSuperAdmin);

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80"
      aria-label="Primary"
    >
      <div className="flex items-stretch justify-around gap-0 px-1 pt-1 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {navItems.map((item) => {
          const Icon = item.icon;
          const end = item.href === "/" || item.href === "/admin";
          return (
            <NavLink
              key={item.href}
              to={item.href!}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex min-h-11 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1 text-[10px] font-medium text-muted-foreground transition-colors [@media(pointer:coarse)]:min-h-12",
                  isActive && "text-primary bg-primary/10",
                )
              }
            >
              <Icon className="h-5 w-5 shrink-0" aria-hidden />
              <span className="truncate max-w-full">{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
